import React from 'react';
import AntLocalization from '../../utils/AntLocalization';

// ─── Helpers ────────────────────────────────────────────────────
export function getInitial(name) {
    if (!name) return '?';
    var parts = name.trim().split(/\s+/);
    return parts[parts.length - 1].charAt(0).toUpperCase();
}

export function formatTime(ts) {
    if (!ts) return '';
    var ms = ts < 1e12 ? ts * 1000 : ts;
    var diff = Math.floor((Date.now() - ms) / 1000);
    if (diff < 60) return AntLocalization.translateText({ vi: 'Vừa xong', en: 'Just now', fr: 'À l\'instant', es: 'Ahora', id: 'Baru saja', ja: 'たった今', zh: '刚刚', ko: '방금', hi: 'अभी', ar: 'الآن' });
    if (diff < 3600) return Math.floor(diff / 60) + AntLocalization.translateText({ vi: ' phút', en: 'm', fr: ' min', es: ' min', id: ' mnt', ja: '分', zh: '分钟', ko: '분', hi: ' मि', ar: ' د' });
    if (diff < 86400) return Math.floor(diff / 3600) + AntLocalization.translateText({ vi: ' giờ', en: 'h', fr: ' h', es: ' h', id: ' jam', ja: '時間', zh: '小时', ko: '시간', hi: ' घं', ar: ' س' });
    if (diff < 604800) return Math.floor(diff / 86400) + AntLocalization.translateText({ vi: ' ngày', en: 'd', fr: ' j', es: ' d', id: ' hr', ja: '日', zh: '天', ko: '일', hi: ' दिन', ar: ' ي' });
    var d = new Date(ms);
    return d.toLocaleDateString('vi-VN');
}

// ─── Markdown renderer ─────────────────────────────────────────
export function renderMarkdown(text) {
    if (!text) return '';
    var html = text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/```([^`]*?)```/gs, '<pre><code>$1</code></pre>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/^### (.+)$/gm, '<h4>$1</h4>')
        .replace(/^## (.+)$/gm, '<h3>$1</h3>')
        .replace(/^# (.+)$/gm, '<h2>$1</h2>')
        .replace(/^[\-\*] (.+)$/gm, '<li>$1</li>')
        .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
        .replace(/(<li>.*<\/li>\n?)+/g, function (match) { return '<ul>' + match + '</ul>'; })
        .replace(/\n/g, '<br/>');
    return html;
}

// ─── MessageBlock styles ─────────────────────────────────────────
var s = {
    tableWrap: { overflowX: 'auto', margin: '4px 0', fontSize: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
    tableTh: { background: '#f8f9fb', padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', fontSize: 11 },
    tableTd: { padding: '5px 8px', borderBottom: '1px solid #f1f5f9', color: '#1e293b', fontSize: 12 },
    actionBtns: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
    actionBtn: { border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', color: '#6366f1', fontWeight: 500 },
    voucherCard: { background: '#fafaff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', margin: '4px 0' },
    voucherHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
    voucherType: { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' },
    voucherStatus: { fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 10, border: '1px solid' },
    voucherCode: { fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 6 },
    voucherRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569', padding: '2px 0' },
    voucherLabel: { color: '#94a3b8' },
    voucherAmount: { fontWeight: 600, color: '#1e293b' },
    voucherLink: { border: 'none', background: 'transparent', color: '#6366f1', cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '4px 0', marginTop: 4 },
    statusBadge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500 },
    statusSuccess: { background: '#f0fdf4', color: '#16a34a' },
    statusError: { background: '#fef2f2', color: '#dc2626' },
    statusInfo: { background: '#eff6ff', color: '#2563eb' },
    textBlock: { lineHeight: 1.6 }
};

// ─── MessageBlock (rich AI content) ─────────────────────────────
export function MessageBlock(props) {
    var block = props.block;
    var onAction = props.onAction;
    var onNavigate = props.onNavigate;

    if (block.type === 'text') {
        var html = renderMarkdown(block.text || '');
        return <div style={s.textBlock} dangerouslySetInnerHTML={{ __html: html }} />;
    }

    if (block.type === 'table') {
        return (
            <div style={s.tableWrap}>
                <table style={s.table}>
                    {block.headers && (
                        <thead>
                            <tr>
                                {block.headers.map(function (h, i) {
                                    return <th key={i} style={s.tableTh}>{h}</th>;
                                })}
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {(block.rows || []).map(function (row, ri) {
                            return (
                                <tr key={ri}>
                                    {row.map(function (cell, ci) {
                                        return <td key={ci} style={s.tableTd}>{cell}</td>;
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    if (block.type === 'action') {
        return (
            <div style={s.actionBtns}>
                {(block.actions || []).map(function (a) {
                    return (
                        <button
                            key={a.key}
                            style={s.actionBtn}
                            onClick={function () { if (onAction) onAction(a.key); }}
                        >
                            {a.label}
                        </button>
                    );
                })}
            </div>
        );
    }

    if (block.type === 'voucher_card') {
        var vc = block;
        var statusColor = vc.status === 'New' ? '#64748b' : vc.status === 'Release' ? '#2563eb' : vc.status === 'Open' ? '#d97706' : vc.status === 'Close' ? '#16a34a' : vc.status === 'Cancel' ? '#dc2626' : '#64748b';
        return (
            <div style={s.voucherCard}>
                <div style={s.voucherHeader}>
                    <span style={s.voucherType}>{vc.label || vc.service}</span>
                    <span style={Object.assign({}, s.voucherStatus, { color: statusColor, borderColor: statusColor })}>{vc.status || 'New'}</span>
                </div>
                <div style={s.voucherCode}>{vc.code || '\u2014'}</div>
                {vc.entity_name && <div style={s.voucherRow}><span style={s.voucherLabel}>{AntLocalization.translateText({ vi: 'Đối tác', en: 'Partner', fr: 'Partenaire', es: 'Socio', id: 'Mitra', ja: '取引先', zh: '往来单位', ko: '거래처', hi: 'पार्टनर', ar: 'الشريك' })}</span><span>{vc.entity_name}</span></div>}
                {vc.sale_mode && <div style={s.voucherRow}><span style={s.voucherLabel}>{AntLocalization.translateText({ vi: 'Chế độ bán', en: 'Sale mode', fr: 'Mode de vente', es: 'Modo de venta', id: 'Mode jual', ja: '販売モード', zh: '销售模式', ko: '판매 모드', hi: 'बिक्री मोड', ar: 'وضع البيع' })}</span><span>{vc.sale_mode}</span></div>}
                {vc.pmt_type && <div style={s.voucherRow}><span style={s.voucherLabel}>{AntLocalization.translateText({ vi: 'Thanh toán', en: 'Payment', fr: 'Paiement', es: 'Pago', id: 'Pembayaran', ja: '支払い', zh: '支付', ko: '결제', hi: 'भुगतान', ar: 'الدفع' })}</span><span>{vc.pmt_type}</span></div>}
                {vc.warehouse_name && <div style={s.voucherRow}><span style={s.voucherLabel}>{AntLocalization.translateText({ vi: 'Kho', en: 'Warehouse', fr: 'Entrepôt', es: 'Almacén', id: 'Gudang', ja: '倉庫', zh: '仓库', ko: '창고', hi: 'गोदाम', ar: 'المستودع' })}</span><span>{vc.warehouse_name}</span></div>}
                {vc.total != null && <div style={s.voucherRow}><span style={s.voucherLabel}>{AntLocalization.translateText({ vi: 'Tổng tiền', en: 'Total', fr: 'Total', es: 'Total', id: 'Total', ja: '合計', zh: '合计', ko: '합계', hi: 'कुल', ar: 'الإجمالي' })}</span><span style={s.voucherAmount}>{typeof vc.total === 'number' ? new Intl.NumberFormat('vi-VN').format(vc.total) : vc.total}</span></div>}
                {vc.items_count != null && vc.items_count > 0 && <div style={s.voucherRow}><span style={s.voucherLabel}>{AntLocalization.translateText({ vi: 'Số dòng', en: 'Items', fr: 'Lignes', es: 'Líneas', id: 'Baris', ja: '明細数', zh: '行数', ko: '항목 수', hi: 'पंक्तियाँ', ar: 'البنود' })}</span><span>{vc.items_count}</span></div>}
                {vc.description && <div style={s.voucherRow}><span style={s.voucherLabel}>{AntLocalization.translateText({ vi: 'Ghi chú', en: 'Note', fr: 'Note', es: 'Nota', id: 'Catatan', ja: 'メモ', zh: '备注', ko: '메모', hi: 'नोट', ar: 'ملاحظة' })}</span><span>{vc.description}</span></div>}
                {vc.created_at && <div style={s.voucherRow}><span style={s.voucherLabel}>{AntLocalization.translateText({ vi: 'Ngày tạo', en: 'Created', fr: 'Créé le', es: 'Creado', id: 'Dibuat', ja: '作成日', zh: '创建日期', ko: '생성일', hi: 'बनाया गया', ar: 'الإنشاء' })}</span><span>{formatTime(vc.created_at)}</span></div>}
                {vc.drilldown && <button style={s.voucherLink} onClick={function () { if (onNavigate) onNavigate(vc.drilldown); }}>{AntLocalization.translateText({ vi: 'Xem chi tiết →', en: 'View detail →', fr: 'Voir le détail →', es: 'Ver detalle →', id: 'Lihat detail →', ja: '詳細を見る →', zh: '查看详情 →', ko: '상세 보기 →', hi: 'विवरण देखें →', ar: 'عرض التفاصيل →' })}</button>}
            </div>
        );
    }

    if (block.type === 'item_list') {
        return (
            <div style={s.tableWrap}>
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.tableTh}>#</th>
                            <th style={s.tableTh}>{AntLocalization.translateText({ vi: 'Sản phẩm', en: 'Item', fr: 'Article', es: 'Artículo', id: 'Produk', ja: '商品', zh: '商品', ko: '상품', hi: 'उत्पाद', ar: 'المنتج' })}</th>
                            <th style={Object.assign({}, s.tableTh, { textAlign: 'right' })}>{AntLocalization.translateText({ vi: 'SL', en: 'Qty', fr: 'Qty', es: 'Qty', id: 'Qty', ja: 'Qty', zh: 'Qty', ko: 'Qty', hi: 'Qty', ar: 'Qty' })}</th>
                            {block.showPrice && <th style={Object.assign({}, s.tableTh, { textAlign: 'right' })}>{AntLocalization.translateText({ vi: 'Đơn giá', en: 'Price', fr: 'Prix', es: 'Precio', id: 'Harga', ja: '単価', zh: '单价', ko: '단가', hi: 'दर', ar: 'السعر' })}</th>}
                            {block.showPrice && <th style={Object.assign({}, s.tableTh, { textAlign: 'right' })}>{AntLocalization.translateText({ vi: 'Thành tiền', en: 'Amount', fr: 'Montant', es: 'Importe', id: 'Jumlah', ja: '金額', zh: '金额', ko: '금액', hi: 'राशि', ar: 'المبلغ' })}</th>}
                            {block.showMatch && <th style={s.tableTh}>{AntLocalization.translateText({ vi: 'Trạng thái', en: 'Status', fr: 'Statut', es: 'Estado', id: 'Status', ja: '状態', zh: '状态', ko: '상태', hi: 'स्थिति', ar: 'الحالة' })}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {(block.items || []).map(function (it, idx) {
                            return (
                                <tr key={idx}>
                                    <td style={s.tableTd}>{idx + 1}</td>
                                    <td style={s.tableTd}>{it.item_name || it.name}{it.description ? <div style={{ fontSize: 10, color: '#94a3b8' }}>{it.description}</div> : null}</td>
                                    <td style={Object.assign({}, s.tableTd, { textAlign: 'right' })}>{it.order_qty || it.qty || 1}</td>
                                    {block.showPrice && <td style={Object.assign({}, s.tableTd, { textAlign: 'right' })}>{it.unit_price ? new Intl.NumberFormat('vi-VN').format(it.unit_price) : '\u2014'}</td>}
                                    {block.showPrice && <td style={Object.assign({}, s.tableTd, { textAlign: 'right' })}>{it.unit_price && it.order_qty ? new Intl.NumberFormat('vi-VN').format(it.unit_price * (it.order_qty || 1)) : '\u2014'}</td>}
                                    {block.showMatch && <td style={s.tableTd}>{it.matched ? <span style={{ color: '#16a34a' }}>\u2713</span> : it.matched === false ? <span style={{ color: '#d97706' }}>?</span> : ''}</td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    if (block.type === 'status') {
        var variant = block.variant || 'info';
        var badgeStyle = Object.assign({}, s.statusBadge,
            variant === 'success' ? s.statusSuccess : variant === 'error' ? s.statusError : s.statusInfo
        );
        return (
            <div style={badgeStyle}>
                {variant === 'success' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                )}
                {variant === 'error' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                )}
                {variant === 'info' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                )}
                <span>{block.text}</span>
            </div>
        );
    }

    return null;
}
